import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import OfferPartnerVerification from './OfferPartnerVerification';
import OfferPartnerVerificationSummary from './OfferPartnerVerificationSummary';
import { loadPartnerVerifications } from '../actionCreators';
import { buildDMSDownloadUrl } from 'features/document/edit/documents/actionCreators';
import { showError } from 'actions/actionCreators/snackbarActions';
import OfferPartnerVerificationToolbar from './OfferPartnerVerificationToolbar';
import { Grid } from '@mui/material';
import debounce from 'lodash.debounce';
import { RightsContext } from 'providers/RightsContext';
import { emptyString } from 'constants/emptyConst';
import Modal from 'features/document/edit/verification/components/ManualInterrogation/components/Modal';
import { getPagingLimits, filterManualVerificationsByValue, filterByValue } from 'features/document/edit/verification/common/functions';
import { sortList } from 'utils/functions';
import { DoneAll } from '@mui/icons-material';
import { getOfferPartnerVerifications, getVerificationIsLoaded } from '../selectors';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { CollapseCard, Button } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import WrappedFakeText from 'components/common/WrappedFakeText';

const OfferPartnerVerificationContainer = React.memo(({ documentId, siteId, partnerId, assetVerificationsVisible }) => {
  const { readOnlyFull, clientVerificationsWrite } = useContext(RightsContext);
  const [t] = useTranslation();

  const verifications = useSelector(state => getOfferPartnerVerifications(state, partnerId), shallowEqual);
  const isLoaded = useSelector(state => getVerificationIsLoaded(state, partnerId), shallowEqual);

  const [cardExpanded, setCardExpanded] = useState(false);
  const onExpand = (_, expanded) => {
    setCardExpanded(!expanded);
  };

  const DEBOUNCE_DELAY = useRef(500);
  const rowsPerPageOptions = useRef([5, 10, 20, 50]);
  const defaultPager = useRef({ page: 1, pageSize: 10 });

  const verificationSorterOptions = [
    { id: 1, name: t('OfferEdit.Client.Verification.Date'), orderByProperty: 'date', descending: true },
    { id: 2, name: t('OfferEdit.Client.Verification.Partner'), orderByProperty: 'partnerName', descending: false },
    { id: 3, name: t('OfferEdit.Client.Verification.Type'), orderByProperty: 'verificationType', descending: false }
  ];

  const defaultVerificationSorterOption = {
    id: 1,
    name: t('OfferEdit.Client.Verification.Date'),
    orderByProperty: 'date',
    descending: true
  };

  const [currentVerificationOrder, setCurrentVerificationOrder] = useState(defaultVerificationSorterOption);

  const dispatch = useDispatch();

  const [verificationList, setVerificationList] = useState(verifications);
  const [verificationsOnPage, setVerificationsOnPage] = useState(verifications);

  const [pager, setPager] = useState(defaultPager.current);
  const [totalCount, setTotalCount] = useState(0);

  const [filter, setFilter] = useState(emptyString);

  const [showManualInterrogationModal, setShowManualInterrogationModal] = useState(false);

  const componentIsMounted = useRef(false);

  useEffect(() => {
    componentIsMounted.current = true;
    return () => {
      componentIsMounted.current = false;
    };
  }, []);

  const toggleManualInterrogationModal = () => setShowManualInterrogationModal(prevShowInterrogationModal => !prevShowInterrogationModal);

  const getVerifications = useCallback(() => {
    let newVerifications = [...verifications];

    //filter partner type verifications
    if (!assetVerificationsVisible) newVerifications = newVerifications.filter(item => item.entityType === 'Partner');

    return newVerifications.map(ver => ({ ...ver, isSelected: false }));
  }, [assetVerificationsVisible, verifications]);

  useEffect(() => {
    if (cardExpanded && !isLoaded) dispatch(loadPartnerVerifications(documentId, siteId, partnerId));
  }, [cardExpanded, isLoaded, dispatch, documentId, siteId, partnerId]);

  useEffect(() => {
    const { indexOfFirstItem, indexOfLastItem } = getPagingLimits(pager);

    let newVerifications = getVerifications();
    newVerifications = sortList(newVerifications, currentVerificationOrder.orderByProperty, currentVerificationOrder.descending);
    setVerificationList(newVerifications);

    setVerificationsOnPage(newVerifications.slice(indexOfFirstItem, indexOfLastItem));
  }, [verifications, getVerifications, pager, currentVerificationOrder.orderByProperty, currentVerificationOrder.descending]);

  useEffect(() => {
    if (isLoaded) {
      const pagingLimits = getPagingLimits(pager);
      setVerificationsOnPage(verificationList.slice(pagingLimits.indexOfFirstItem, pagingLimits.indexOfLastItem));
      setTotalCount(verificationList.length);
    }
  }, [pager, isLoaded, verificationList]);

  const downloadFile = async fileId => {
    try {
      let link = dispatch(buildDMSDownloadUrl(fileId));
      window.location = link;
    } catch (error) {
      // continue regardless of error
    }
  };

  const showSensitiveInfoError = () => {
    dispatch(showError(t('OfferEdit.Client.Verification.SensitiveInfoError')));
  };

  const pushFiltersAndPaging = useCallback(
    (filter, pager) => {
      setFilter(filter);
      setPager(pager);

      const { indexOfFirstItem, indexOfLastItem } = getPagingLimits(pager);

      if (filter !== emptyString) {
        const filteredVerifications = filterByValue(verificationList, filter);
        const currentVerifications = filteredVerifications.slice(indexOfFirstItem, indexOfLastItem);

        setVerificationsOnPage(currentVerifications);
        setTotalCount(filteredVerifications.length);
      } else {
        setVerificationsOnPage(verificationList.slice(indexOfFirstItem, indexOfLastItem));
        setTotalCount(verificationList.length);
      }
    },
    [verificationList]
  );

  useEffect(() => {
    pushFiltersAndPaging(filter, pager);
  }, [pager, filter, pushFiltersAndPaging]);

  const onFilterValueChangedDebounce = _filter => setPager(defaultPager.current);

  const filterChangedDebounced = useRef(debounce(onFilterValueChangedDebounce, DEBOUNCE_DELAY.current));

  const handleChangePage = page => {
    let newPager = Object.assign({}, pager, { page: page + 1, pageSize: pager.pageSize });
    pushFiltersAndPaging(filter, newPager);
  };

  const handleChangeRowsPerPage = pageSize => {
    let newPager = Object.assign({}, pager, { pageSize: pageSize, page: 1 });
    pushFiltersAndPaging(filter, newPager);
  };

  const handleRefresh = () => setPager(defaultPager);

  const handleFilterValueChanged = (value, debounced) => {
    setFilter(value);

    if (debounced) filterChangedDebounced.current(value);
    else setPager(defaultPager.current);
  };

  const handleResetFilters = () => {
    setFilter(emptyString);
    setPager(defaultPager.current);
  };

  const handleVerificationSorterChanged = id => {
    const selectedVerificationOrder = { ...verificationSorterOptions.find(x => x.id === id) };
    setVerificationList(previousState =>
      sortList(previousState, selectedVerificationOrder.orderByProperty, selectedVerificationOrder.descending)
    );
    setCurrentVerificationOrder(selectedVerificationOrder);
  };

  return (
    <>
      <CollapseCard
        id={'offerPartnerVerificationCard'}
        icon={DoneAll}
        title={t('OfferEdit.Client.Verification.Title')}
        subheader={<OfferPartnerVerificationSummary documentId={documentId} siteId={siteId} partnerId={partnerId} />}
        hideSubheaderOnExpand
        toggleOnHeaderClick
        onToggle={onExpand}
        expanded={cardExpanded}
      >
        {!isLoaded && cardExpanded ? (
          <WrappedFakeText />
        ) : (
          <Grid container>
            <Grid item xs={12} sx={{ overflowX: 'auto' }}>
              <Grid item xs={12}>
                <OfferPartnerVerificationToolbar
                  fullTextFilter={filter}
                  onFilterValueChanged={handleFilterValueChanged}
                  onResetFilters={handleResetFilters}
                  onVerificationSorterChanged={handleVerificationSorterChanged}
                  verificationSorterOptions={verificationSorterOptions}
                  selectedVerificationOrder={currentVerificationOrder}
                />
              </Grid>
              <CardNoResultsWrapper content={verificationsOnPage}>
                <OfferPartnerVerification
                  data={verificationsOnPage}
                  downloadFile={downloadFile}
                  showSensitiveInfoError={showSensitiveInfoError}
                />
              </CardNoResultsWrapper>
            </Grid>
            <Grid container justifyContent='space-between' alignItems='center' mt={2}>
              <Button
                id={'offerPartnerVerification_manualInterrogationBtn'}
                onClick={() => toggleManualInterrogationModal()}
                disabled={!verificationList || !clientVerificationsWrite || readOnlyFull}
              >
                {t('OfferEdit.Client.Verification.ManualInterrogation')}
              </Button>
              <Grid item>
                <PaginationWrapper
                  count={totalCount}
                  pageSize={pager.pageSize}
                  page={pager.page}
                  rowsPerPageOptions={rowsPerPageOptions.current}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  onPageChange={handleChangePage}
                  onRefresh={handleRefresh}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
      </CollapseCard>
      {showManualInterrogationModal && (
        <Modal
          allVerifications={verifications}
          closePartnerManualInterrogationModal={toggleManualInterrogationModal}
          documentId={documentId}
          siteId={siteId}
          partnerId={partnerId}
          getPagingLimits={getPagingLimits}
          filterByValue={filterManualVerificationsByValue}
        />
      )}
    </>
  );
});

OfferPartnerVerificationContainer.displayName = 'OfferPartnerVerificationContainer';
OfferPartnerVerificationContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  partnerId: PropTypes.number.isRequired,
  assetVerificationsVisible: PropTypes.bool
};

export default OfferPartnerVerificationContainer;
